import { createContext, useContext, useEffect, useState } from "react";

const LanguageContext = createContext();

export const translations = {
  en: {
    // Navigation
    nav: {
      title: "Job Hunter",
      home: "Home",
      dashboard: "Dashboard",
      analytics: "Analytics",
      applyService: "Apply Service",
      terms: "Terms of Use",
      privacy: "Privacy Policy",
    },

    auth: {
      login: "Log in",
      logout: "Log out",
      loginRequired: "Login required",
      loginToViewDashboard: "Please log in to view the dashboard.",
      loginToViewAnalytics:
        "Please log in to unlock the rest of the analytics.",
      loginCta: "Log in now",
    },

    login: {
      title: "Job Hunter Dashboard",
      subtitle: "Track your job applications with powerful analytics",
      welcome: "Welcome Back",
      signInWith: "Sign in with Google to continue",
      features: {
        title: "What You Get",
        analytics: "Real-time Analytics",
        analyticsDesc: "Track job market trends and application success rates",
        tracking: "Application Management",
        trackingDesc: "Organize and monitor all your job applications",
        insights: "Market Insights",
        insightsDesc: "Get data-driven insights to improve your job search",
      },
      footer: "By signing in, you agree to our",
      secureAuth: "Secure authentication with Google",
    },

    // Home Page
    home: {
      hero: {
        title: "🎓 Makes Job Offers Simple",
        subtitle: "All-in-One Platform, Powered by AI.",
        exploreJobs: "Explore Jobs",
        viewAnalytics: "View Analytics",
      },
      features: {
        title: "Why Choose Job Hunter to Get Offers?",
        smartDashboard: {
          title: "Smart Dashboard",
          desc: "Track all your job applications in one place with real-time updates and insights.",
        },
        analytics: {
          title: "Analytics & Trends",
          desc: "Visualize job market trends, top hiring companies, and category distributions.",
        },
        aiInsights: {
          title: "AI-Powered Insights",
          desc: "Get intelligent recommendations and insights powered by advanced AI technology.",
        },
        fast: {
          title: "Lightning Fast",
          desc: "Search through thousands of jobs instantly with optimized performance.",
        },
        secure: {
          title: "Secure & Private",
          desc: "Your data is safe with us. We prioritize your privacy and security.",
        },
        growth: {
          title: "Career Growth",
          desc: "Accelerate your career journey with personalized job recommendations.",
        },
      },
      stats: {
        jobs: "Job Opportunities",
        companies: "Top Companies",
        categories: "Job Categories",
      },
      cta: {
        title: "Ready to Find Your Dream Job?",
        subtitle:
          "Our Apply Service finds roles, submits tailored applications, and keeps you updated so you can focus on acing the interviews.",
        button: "Get Started Now",
      },
    },

    // Apply Page
    apply: {
      hero: {
        title: "Focus on What Matters Most",
        subtitle: "We'll Handle Your Resume Distribution with Care",
        question: "Exhausted from Sending Out Resumes?",
        desc: "24/7 we got you covered",
        cta: "Start Saving Time - Get Started Now",
      },
      products: {
        title: "Why Choose Our Service",
        product1: {
          title: "Save 3 hours / day",
          tip1: "Your time is precious! With 8 hours for sleep, 8 hours for classes and homework, and 2 hours for meals, you only have 6 hours left for your job search.",
          tip2: "1 offer = 500 resumes = 3 hours/day spent applying",
          tip3: "Instead of spending hundreds of hours on resume submissions, let us handle it for you! Use your time wisely by preparing for interviews—it's smarter!",
        },
        product2: {
          title: "1on1 Care Skip the Hassle",
          tip1: "1-on-1 manual resume submission—no opportunities missed!",
          tip2: "Stop wasting time searching for jobs. job hunter brings you an average of 2,000 new monthly positions, covering major companies and unicorns across North America!",
          tip3: "We've followed thousands of jobs—the fastest and most comprehensive job board tailored for international students.",
        },
        product3: {
          title: "As low as $0.3 per position",
          tip1: "In 2023, job hunter tracked 100K+ tech positions. Submitting to just 500 of these would take at least 3 hours a day; submitting to all would require 30 hours daily.",
          tip2: "Package 1: Exclusive Job Listings: $0.3 per position",
          tip3: "Package 2: Dedicated Submission Expert: $0.4 per position",
        },
      },
      testimonials: {
        title: "10,000+ of Students benefit from",
        subtitle: "Apply for Me™",
        cta: "Start Your Success Story Today",
      },
      faq: {
        title: "Frequently Asked Questions",
        subtitle:
          "Got Questions? We've Got Answers. Find out everything you need to know about our resume distribution service.",
        q1: "What is your resume distribution service?",
        a1: "Our resume distribution service provides 1-on-1 support, where we manually submit your resume to targeted employers and recruiters based on your industry and job preferences. We also offer real-time tracking so you can monitor the progress of your applications. This means your application is seen by the right people, without you spending hours on tedious tasks. Focus on preparing for interviews, and let us handle the rest—start today and get ahead of the competition!",
        q2: "How long does it take to see results?",
        a2: "While results can vary, many of our users start receiving interview responses within a few days to a couple of weeks. The faster you start, the sooner you could be interviewing for your dream job. Don't delay—take the first step now and see how quickly you can advance in your career!",
        consultMore: "Consult More Questions",
      },
      modal: {
        title: "Tired of applying? Let us do it for you.",
        benefit1:
          "Save 10+ hours daily — we've got you covered 24/7 while you focus on preparing for interviews",
        benefit2:
          "Let our Experts apply for you—boosting your interview chances by 5x.",
        benefit3: "Only $0.3 per job — no hidden fees, cancel anytime",
        cta: "Add us on WeChat now — start applying in 1 minute.",
        wechat: "WeChat",
        trust: "Trusted by 10,000+ students. No spam.",
      },
    },

    // Job Status Page
    jobStatus: {
      title: "Job Status Query",
      subtitle: "Browse the latest opportunities curated just for you",
      selectType: "Select Job Type:",
      jobInfo: "Job Information",
      recent: "(Last Month)",
      found: "Found",
      jobs: "jobs",
      loading: "Loading job information...",
      noJobs: "No related job information for",
      showing: "Showing",
      to: "-",
      of: "of",
      records: "records",
      page: "Page",
      copyLink: "Copy Link",
      viewDetails: "View Details",
      visitJob: "Visit Job Page",
      copied: "Link copied",
      copyFailed: "Copy failed",
      fetchError: "Failed to fetch job information:",
      jobTypes: {
        software: "Software Engineer",
        data_analyst: "Data Analyst",
        data_scientist: "Data Scientist",
        data_engineer: "Data Engineer",
        ml_engineer: "Machine Learning Engineer",
        pm: "Product Manager",
      },
      loginRequiredTitle: "Login required",
      loginRequiredDescription:
        "Please log in to view the dashboard and unlock all job details.",
      loginCta: "Log in to view dashboard",
      filterByLocation: "Filter by Location",
      filterByCompany: "Filter by Company",
      allLocations: "All Locations",
      allCompanies: "All Companies",
      clearFilters: "Clear",
      selectLocation: "Select Location",
      selectCompany: "Select Company",
    },

    // Analytics Page
    analytics: {
      title: "Analytics Report",
      subtitle: "Insights and trends from the job market based on recent data",
      loading: "Loading analytics data...",
      noData: "No analytics data",
      fetchError: "Failed to fetch analytics data:",
      recent: "Current Month",
      trendWindow: "Trend Window",
      statsLabel: "Statistics period",
      topCompanies: {
        title: "Top Hiring Companies Top 20",
        top1to10: "🏆 Top 1-10",
        top11to20: "📊 Top 11-20",
        noData: "No company data",
        rank: "Rank",
        company: "Company Name",
        jobCount: "Job Count",
      },
      monthlyTrend: {
        title: "Monthly Job Posting Trends",
        noData: "No monthly trend data",
        jobCount: "Job Count",
        label: "Job Count",
        month: "Month",
      },
      categoryTrend: {
        title: "Monthly Job Category Trends",
        noData: "No monthly category trend data",
        software: "Software Engineering",
        dataEngineer: "Data Engineering",
        ml: "Machine Learning",
        dataScience: "Data Science",
        dataAnalyst: "Data Analysis",
        jobCount: "Job Count",
        month: "Month",
      },
      categories: {
        title: "Job Category Distribution",
        noData: "No job category data",
        software: "Software Engineering",
        dataEngineer: "Data Engineering",
        ml: "Machine Learning",
        dataScience: "Data Science",
        dataAnalyst: "Data Analysis",
        names: {
          软件工程: "Software Engineering",
          数据工程: "Data Engineering",
          机器学习: "Machine Learning",
          数据科学: "Data Science",
          数据分析: "Data Analysis",
        },
      },
      limitedAccess: {
        title: "Limited preview",
        description: "Log in to unlock the full analytics dashboard.",
        latestJobTitle: "Latest tracked job insight",
        jobCountLabel: "Job postings last month",
        cta: "Log in for full analytics",
      },
    },
    legal: {
      termsTitle: "Terms of Use",
      privacyTitle: "Privacy Policy",
      termsContent: [
        "Welcome to Job Hunter. By using our platform, you agree to these simple terms.",
        "Usage: Please use our services for personal job search purposes only.",
        "Liability: We provide this platform 'as-is'. While we strive for accuracy, we are not responsible for any outcomes, errors, or losses resulting from your use of the dashboard or job applications.",
        "Account: You are responsible for maintaining the security of your Google-linked account.",
        "Changes: We may update these terms as the platform evolves.",
      ],
      privacyContent: [
        "Your privacy matters to us. Here is how we handle your data:",
        "Data Collection: We only collect essential information required for authentication (via Google) and application tracking.",
        "Usage: Your data is used solely to provide and improve our services. We do not sell your personal information.",
        "Security: We use industry-standard security measures to protect your information.",
        "Choices: You can stop using the service at any time. Your data is handled according to our standard procedures.",
      ],
    },
  },

  cn: {
    // Navigation
    nav: {
      title: "求职猎人",
      home: "首页",
      dashboard: "职位状态",
      analytics: "职位分析",
      applyService: "申请服务",
      terms: "使用条款",
      privacy: "隐私政策",
    },

    auth: {
      login: "登录",
      logout: "退出登录",
      loginRequired: "需要登录",
      loginToViewDashboard: "请登录后查看仪表盘。",
      loginToViewAnalytics: "请登录以解锁完整的数据分析。",
      loginCta: "立即登录",
    },

    login: {
      title: "职位猎手仪表盘",
      subtitle: "用强大的分析工具追踪您的求职申请",
      welcome: "欢迎回来",
      signInWith: "使用 Google 登录以继续",
      features: {
        title: "功能特性",
        analytics: "实时分析",
        analyticsDesc: "追踪职位市场趋势和申请成功率",
        tracking: "申请管理",
        trackingDesc: "组织和监控所有求职申请",
        insights: "市场洞察",
        insightsDesc: "获取数据驱动的洞察以改善求职",
      },
      footer: "登录即表示您同意我们的",
      secureAuth: "Google 安全认证登录",
    },

    // Home Page
    home: {
      hero: {
        title: "🎓 让Offer手到擒来",
        subtitle: "AI 驱动的一体化平台",
        exploreJobs: "探索职位",
        viewAnalytics: "查看分析",
      },
      features: {
        title: "为什么选择Job Hunter来Get Offers？",
        smartDashboard: {
          title: "智能职位状态查询",
          desc: "在一个地方跟踪所有求职申请，实时更新和洞察。",
        },
        analytics: {
          title: "分析与趋势",
          desc: "可视化就业市场趋势、顶级招聘公司和类别分布。",
        },
        aiInsights: {
          title: "AI 智能洞察",
          desc: "获取由先进AI技术驱动的智能推荐和洞察。",
        },
        fast: {
          title: "闪电般快速",
          desc: "通过优化性能即时搜索数千个职位。",
        },
        secure: {
          title: "安全与隐私",
          desc: "您的数据在我们这里是安全的。我们优先考虑您的隐私和安全。",
        },
        growth: {
          title: "职业成长",
          desc: "通过个性化职位推荐加速您的职业之旅。",
        },
      },
      stats: {
        jobs: "职位机会",
        companies: "顶级公司",
        categories: "职位类别",
      },
      cta: {
        title: "准备好找到您的理想工作了吗？",
        subtitle:
          "我们的申请服务为您寻找职位、定制投递并实时更新进展，节约您的时间，让您专注于准备面试和刷题。",
        button: "立即开始",
      },
    },

    // Apply Page
    apply: {
      hero: {
        title: "专注于最重要的事情",
        subtitle: "我们将用心处理您的简历投递",
        question: "厌倦了投简历？",
        desc: "我们全天候为您服务",
        cta: "开始节省时间 - 立即开始",
      },
      products: {
        title: "为什么选择我们的服务",
        product1: {
          title: "每天节省3小时",
          tip1: "您的时间很宝贵！8小时睡眠、8小时上课和作业、2小时吃饭，您只剩下6小时用于求职。",
          tip2: "1个offer = 500份简历 = 每天3小时申请时间",
          tip3: "不要花费数百小时投递简历，让我们为您处理！明智地利用时间准备面试更聪明！",
        },
        product2: {
          title: "一对一服务 跳过麻烦",
          tip1: "一对一人工简历投递——不错过任何机会！",
          tip2: "不要浪费时间搜索工作。求职猎人每月为您带来平均2,000个新职位，覆盖北美主要公司和独角兽！",
          tip3: "我们追踪了数千个工作——为国际学生量身定制的最快、最全面的职位板。",
        },
        product3: {
          title: "低至每个职位$0.3",
          tip1: "2023年，求职猎人追踪了10K+科技相关的职位. 仅投递500个就需要每天至少3小时；投递全部需要每天30小时。",
          tip2: "套餐1：独家职位列表：每个职位$0.3",
          tip3: "套餐2：专属投递专家：每个职位$0.4",
        },
      },
      testimonials: {
        title: "10,000+ 学生受益于",
        subtitle: "Apply for Me™",
        cta: "今天开始您的成功故事",
      },
      faq: {
        title: "常见问题",
        subtitle: "有疑问？我们有答案。了解关于我们简历分发服务的一切。",
        q1: "什么是简历分发服务？",
        a1: "我们的简历分发服务提供一对一支持，根据您的行业和职位偏好，我们会手动将您的简历提交给目标雇主和招聘人员。我们还提供实时跟踪，以便您可以监控申请进度。这意味着您的申请会被合适的人看到，而无需花费数小时做繁琐的工作。专注于准备面试，让我们处理其余的事情——今天就开始，领先竞争对手！",
        q2: "需要多长时间才能看到结果？",
        a2: "虽然结果可能有所不同，但我们的许多用户在几天到几周内就开始收到面试回复。您越早开始，就能越早面试理想工作。不要拖延——现在迈出第一步，看看您能多快推进职业生涯！",
        consultMore: "咨询更多问题",
      },
      modal: {
        title: "厌倦了申请？让我们为您代劳。",
        benefit1: "每天节省10+小时 — 我们全天候为您服务，您专注于准备面试",
        benefit2: "让我们的专家为您申请——将您的面试机会提高5倍。",
        benefit3: "仅需每个职位$0.3 — 无隐藏费用，随时取消",
        cta: "立即添加我们的微信 — 1分钟内开始申请。",
        wechat: "微信",
        trust: "10,000+ 学生信赖。无垃圾信息。",
      },
    },

    // Job Status Page
    jobStatus: {
      title: "职位状态查询",
      subtitle: "浏览为您精选的最新职位机会",
      selectType: "选择职位类型：",
      jobInfo: "职位信息",
      recent: "（最近一个月）",
      found: "共找到",
      jobs: "个职位",
      loading: "正在加载职位信息...",
      noJobs: "暂无",
      showing: "显示第",
      to: "-",
      of: "条，共",
      records: "条记录（第",
      page: "页）",
      copyLink: "复制链接",
      viewDetails: "查看详情",
      visitJob: "访问职位页面",
      copied: "已复制链接",
      copyFailed: "复制失败",
      fetchError: "获取职位信息失败:",
      jobTypes: {
        software: "软件工程师",
        data_analyst: "数据分析师",
        data_scientist: "数据科学家",
        data_engineer: "数据工程师",
        ml_engineer: "机器学习工程师",
        pm: "产品经理",
      },
      loginRequiredTitle: "需要登录",
      loginRequiredDescription: "登录后即可查看仪表盘并解锁所有职位详情。",
      loginCta: "登录查看仪表盘",
      filterByLocation: "按地点筛选",
      filterByCompany: "按公司筛选",
      allLocations: "所有地点",
      allCompanies: "所有公司",
      clearFilters: "清除",
      selectLocation: "选择地点",
      selectCompany: "选择公司",
    },

    // Analytics Page
    analytics: {
      title: "数据分析报告",
      subtitle: "基于最新数据的求职市场洞察及趋势分析",
      loading: "正在加载分析数据...",
      noData: "暂无分析数据",
      fetchError: "获取分析数据失败:",
      recent: "本月",
      trendWindow: "趋势周期",
      statsLabel: "统计周期",
      topCompanies: {
        title: "热门招聘公司 Top 20",
        top1to10: "🏆 Top 1-10",
        top11to20: "📊 Top 11-20",
        noData: "暂无公司数据",
        rank: "排名",
        company: "公司名称",
        jobCount: "职位数量",
      },
      monthlyTrend: {
        title: "月度职位发布趋势",
        noData: "暂无月度趋势数据",
        jobCount: "职位数量",
        label: "职位数量",
        month: "月份",
      },
      categoryTrend: {
        title: "月度职位分类趋势",
        noData: "暂无月度分类趋势数据",
        software: "软件工程",
        dataEngineer: "数据工程",
        ml: "机器学习",
        dataScience: "数据科学",
        dataAnalyst: "数据分析",
        jobCount: "职位数量",
        month: "月份",
      },
      categories: {
        title: "职位分类分布",
        noData: "暂无职位分类数据",
        software: "软件工程",
        dataEngineer: "数据工程",
        ml: "机器学习",
        dataScience: "数据科学",
        dataAnalyst: "数据分析",
        names: {
          软件工程: "软件工程",
          数据工程: "数据工程",
          机器学习: "机器学习",
          数据科学: "数据科学",
          数据分析: "数据分析",
        },
      },
      limitedAccess: {
        title: "预览模式",
        description: "登录后可查看完整的职位分析数据。",
        latestJobTitle: "最新跟踪职位预览",
        jobCountLabel: "上月职位数量",
        cta: "登录查看完整分析",
      },
    },
    legal: {
      termsTitle: "使用条款",
      privacyTitle: "隐私政策",
      termsContent: [
        "欢迎使用求职猎人。使用我们的平台即表示您同意以下条款。",
        "使用限制：请仅将我们的服务用于个人求职目的。",
        "免责声明：平台按“原样”提供。虽然我们努力确保准确性，但对于您使用仪表盘或职位申请产生的任何结果、错误或损失，我们不承担任何法律责任。",
        "账户安全：您有责任维护与 Google 链接账户的安全。",
        "条款变更：随着平台的发展，我们可能会更新这些条款。",
      ],
      privacyContent: [
        "您的隐私对我们至关重要。以下是我们处理数据的方式：",
        "数据收集：我们仅收集身份验证（通过 Google）和职位追踪所需的必要信息。",
        "数据用途：您的数据仅用于提供和改进我们的服务。我们不会出售您的个人信息。",
        "数据安全：我们采用行业标准的安全措施来保护您的信息。",
        "用户选择：您可以随时停止使用服务。我们将按照标准流程处理您的数据。",
      ],
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    const savedLang = localStorage.getItem("language");
    if (savedLang) {
      return savedLang;
    }

    // Detect browser language
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.toLowerCase().startsWith("zh") ? "cn" : "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "cn" : "en"));
  };

  const t = (key) => {
    const keys = key.split(".");
    let value = translations[language];

    for (const k of keys) {
      value = value?.[k];
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
